import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { Observable, Subscription, filter } from "rxjs";
import { AppState } from "src/app/store/app.reducer";
import { selectImportedMtmaDevice } from "src/app/store/user-file/user-file.selector";
import * as UserFileActions from 'src/app/store//user-file/user-file.actions';
import { StandardDesignParameters, StandardNewValidator, StandardPageLayout } from "src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib";
import { ExportInfo } from "src/app/model/export";
import { CustomValidators } from "src/app/validators/custom.validators";
import { MtmaAction } from "src/app/model/mtma-action";
import { selectIsImpersMode } from "src/app/store/user/user.selector";

@Component({
	templateUrl: './mtma-import-dialog.component.html',
})

export class MtmaImportDeviceDialogComponent implements OnInit, OnDestroy {
	isImpersMode$: Observable<boolean | undefined>;
	isImpersModeSub: Subscription | undefined;
	isImpersMode: boolean;
	// filesForm: FormGroup;
	mtmaActionsSub: Subscription;

	mtmaActions: MtmaAction[] = [];

	exportForm: FormGroup;

	import$: Observable<boolean | undefined>;
	importSub: Subscription;

	exportInfo: ExportInfo;
	update: boolean;

	// design
	screenWidth: number;
	MAX_MD_WIDTH = StandardDesignParameters.MAX_MD_WIDTH;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig
	) {
		if (config.data !== undefined && config.data.exportInfo !== undefined) {
			this.exportInfo = config.data.exportInfo;
			this.update = true;
		} else {
			this.exportInfo = new ExportInfo('', '');
			this.update = false;
		}
		this.exportInfo.DefaultAction = 255;
		this.exportInfo.ExportTypes = [
			"MTMA_MODULES",
			"MTMA_CONTACTS",
			"MTMA_UNITS",
			"MTMA_TONES",
			"MTMA_TAGS",
			"MTMA_KEYPADS",
			"MTMA_AC_RULES",
			"MTMA_AC_BADGES",
			"MTMA_AC_KEYPADS",
			"MTMA_AC_USERS",
			"MTMA_UNITS_CONTACTS",
		];
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.buildForm();
		this.onResize();

		StandardNewValidator.setFirstCompile(true);
	}

	ngOnDestroy(): void {
		this.importSub?.unsubscribe();
		this.mtmaActionsSub?.unsubscribe();
		this.isImpersModeSub?.unsubscribe();
	}

	initSelectors(): void {
		this.import$ = this.store.select(selectImportedMtmaDevice);
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
	}

	buildForm(): void {
		const typeArr = this.exportInfo.ExportTypes.map(() => this.fb.control(true));
		this.exportForm = new FormGroup({
			'name': new FormControl(this.exportInfo.Name, [Validators.required, Validators.minLength(5)]),
			'types': this.fb.array(typeArr, CustomValidators.multipleCheckboxRequireOne),
			'Action1': new FormControl(this.exportInfo.DefaultAction, Validators.required),
		});
		this.f.name.disable();
	}

	subscribeAll(): void {
		this.importSub = this.import$?.pipe(filter(res => !!res)).subscribe(
			(res) => {
				if (res) {
					this.closeDialog();
				}
			}
		);
		this.mtmaActionsSub = this.store.select('device', 'mtmaActions')
			.subscribe(mtmaActions => {
				this.mtmaActions = mtmaActions.filter(a => a.Keypads);
			});
		this.isImpersModeSub = this.isImpersMode$?.subscribe(
			(isImpersMode) => {
				this.isImpersMode = isImpersMode;
			}
		);
	}

	get f(): any {
		return this.exportForm.controls;
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.exportForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.exportForm.get(key).errors;
			if (controlErrors != null) {
				Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				});
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	save(): void {
		const WDControl = this.f.types as FormArray;
		const types = [];
		WDControl.controls.forEach((c, i) => {
			if (c.value) {
				types.push(this.exportInfo.ExportTypes[i]);
			}
		});
		const importInfo = { ExportTypes: types, DefaultAction: this.f.Action1.value }
		this.store.dispatch(UserFileActions.importMtmaDevice({ exportId: this.exportInfo.Id, importInfo: importInfo, isImpersMode: this.isImpersMode }));
	}

	closeDialog(): void {
		this.ref.close();
	}

	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: MtmaImportDeviceDialogComponent['f'], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: MtmaImportDeviceDialogComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

	manageRespStyle(classType: string): string {
		return StandardPageLayout.manageRespStyle(classType, this.screenWidth);
	}

	manageRespStylewithError(type: MtmaImportDeviceDialogComponent["f"], classType: string): string {
		return StandardNewValidator.manageRespStylewithError(type, classType, this.screenWidth);
	}

	showErrorRequiredCheckBox(type: MtmaImportDeviceDialogComponent["f"]): string {
		return StandardNewValidator.showErrorRequiredCheckBox(type);
	}

	customBorderRow(): string {
		let inputTextStyle = "";

		if (this.screenWidth <= StandardDesignParameters.MAX_MD_WIDTH) {
			inputTextStyle += "row";
		}
		inputTextStyle += " border-100 border-bottom-0";
		return inputTextStyle;
	}
}